import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import BorderedBtn from "./FacilityBorderedBtn";
import SearchModal, { SearchState } from "./SearchModal";
import { SonicFacilityData } from "../../common/classes/SonicFacility";
import DBListPager from "../../common/db-list/DBListPager";
import LoadingSpinner from "../../common/LoadingSpinner";
import NoResults from "../../common/NoResults";
import ArchiveResultCounter from "../../common/archive/ArchiveResultCounter";
import {
  getDefaultImage,
  parsePrefecture,
  genDisplayCount,
} from "../../utils/GeneralUtils";
import styles from "./FacilityList.module.scss";
import UserContact from "./UserContact";
import ContactBanner from "./ContactBanner";
import SonicLink from "../../common/routing/SonicLink";

type Props = {
  facilities: SonicFacilityData[];
  isLoading: boolean;
  totalEntries: number;
  totalPages: number;
  currentPage: number;
  perView: number;
  keyword: string;
  handleKeyword: (value: string) => void;
  handleSearchFunc: (args: SearchState) => void;
  clearSearch: () => void;
  searchArgs: SearchState;
};

const FacilityList: React.FC<Props> = ({
  facilities,
  isLoading,
  totalEntries,
  totalPages,
  currentPage,
  perView,
  keyword,
  handleKeyword,
  handleSearchFunc,
  clearSearch,
  searchArgs,
}) => {
  const [searchOpen, setSearchOpen] = useState<boolean>(false);
  const [infoOpen, setInfoOpen] = useState<boolean>(false);

  return (
    <div className={styles["f-facilityList"]}>
      {isLoading ? (
        <LoadingSpinner space={1} />
      ) : (
        <div className={styles["inner"]}>
          <BorderedBtn
            onClick={() => setSearchOpen(true)}
            icon={["fas", "magnifying-glass"]}
          />
          <div className={styles["f-facilityList__count"]}>
            全{totalEntries}件
          </div>
          <div className={styles["f-facilityList__info"]}>
            <button
              className={styles["f-facilityList__info-btn"]}
              onClick={() => setInfoOpen(!infoOpen)}
            >
              <FontAwesomeIcon icon={["fas", "info"]} />
            </button>
            <div
              className={`${styles["f-facilityList__info-list"]} ${
                infoOpen ? styles["show"] : ""
              }`}
            >
              <div className={styles["f-facilityList__info-list__head"]}>
                <p>アイコン説明</p>
                <button onClick={() => setInfoOpen(false)}>
                  <FontAwesomeIcon icon={["fas", "times"]} />
                </button>
              </div>
              <div className={styles["f-facilityList__info-list__item"]}>
                <FontAwesomeIcon icon={["fas", "trophy"]} />
                <span>イベント</span>
              </div>
              <div className={styles["f-facilityList__info-list__item"]}>
                <FontAwesomeIcon icon={["fas", "utensils"]} />
                <span>フード提供</span>
              </div>
            </div>
          </div>
          {facilities.length > 0 ? (
            <ul className={styles["f-facilityList__items"]}>
              {facilities.map((item) => (
                <li
                  key={item.id}
                  className={styles["f-facilityList__items-item"]}
                >
                  <SonicLink
                    to={`/facility/${item.id}`}
                    className={styles["f-facilityList__items-item__link"]}
                  >
                    <div
                      className={styles["f-facilityList__items-item__inner"]}
                    >
                      <div
                        className={styles["f-facilityList__items-item__icon"]}
                      >
                        {IconBox({
                          hostEvents: item.hostEvents,
                          foodService: item.foodService,
                        })}
                      </div>
                      <div
                        className={styles["f-facilityList__items-item__imgbox"]}
                      >
                        <div
                          className={styles["f-facilityList__items-item__img"]}
                        >
                          {item.prefecture && (
                            <span>
                              {parsePrefecture(
                                item.prefecture
                              ).toLocaleUpperCase()}
                            </span>
                          )}
                          <img
                            src={
                              item.imgs && item.imgs.length
                                ? item.imgs[0].large.url
                                : getDefaultImage()
                            }
                            alt={item.name}
                          />
                        </div>
                      </div>
                      <p>{item.name}</p>
                    </div>
                  </SonicLink>
                </li>
              ))}
            </ul>
          ) : (
            <NoResults />
          )}
          <ArchiveResultCounter
            current={genDisplayCount(
              currentPage,
              totalPages,
              perView,
              totalEntries
            )}
            total={totalEntries}
          />
          <DBListPager
            totalPage={totalPages}
            currentPage={currentPage}
            shouldScrollToTop={true}
          />
          <div className={styles["f-facilityList__request"]}>
            <ContactBanner />
          </div>
          <div className={styles["f-facilityList__notes"]}>
            <UserContact />
          </div>
        </div>
      )}
      {searchOpen && (
        <SearchModal
          searchOpen
          handleSearchModal={setSearchOpen}
          keyword={keyword}
          handleKeyword={handleKeyword}
          handleSearchFunc={handleSearchFunc}
          clearSearch={clearSearch}
          searchArgs={searchArgs}
        />
      )}
    </div>
  );
};

export default FacilityList;

type BoxProps = {
  hostEvents?: string;
  foodService?: string;
};

const IconBox: React.FC<BoxProps> = ({ hostEvents, foodService }) => {
  return (
    <div className={styles["iconBox"]}>
      <span
        className={`${styles["iconBox__item"]} ${
          hostEvents === "event_true" ? styles["active"] : ""
        }`}
      >
        <FontAwesomeIcon icon={["fas", "trophy"]} />
      </span>
      <span
        className={`${styles["iconBox__item"]} ${
          foodService === "food_true" ? styles["active"] : ""
        }`}
      >
        <FontAwesomeIcon icon={["fas", "utensils"]} />
      </span>
    </div>
  );
};
